// File: src/services/branding.service.ts
import api from './api';

interface Branding {
  domain: string;
  brand_name: string;
  logo_url: string;
  favicon_url: string;
  primary_color: string;
  secondary_color: string;
  login_background_url?: string;
  custom_css?: string;
  metadata?: any;
}

class BrandingService {
  private currentBranding: Branding | null = null;
  private brandingPromise: Promise<Branding> | null = null;

  /**
   * Get current domain branding
   */
  async getCurrentBranding(): Promise<Branding> {
    // Return cached branding if available
    if (this.currentBranding) {
      return this.currentBranding;
    }

    // Return existing promise if already loading
    if (this.brandingPromise) {
      return this.brandingPromise;
    }

    // Start loading branding
    this.brandingPromise = this.loadBranding();
    
    try {
      this.currentBranding = await this.brandingPromise;
      return this.currentBranding;
    } catch (error) {
      // If branding fails, return minimal branding to keep app running
      console.error('Failed to load branding, using minimal fallback:', error);
      this.currentBranding = {
        domain: window.location.hostname,
        brand_name: 'Portal',
        logo_url: '', // Empty logo
        favicon_url: '', // Empty favicon to maintain multi-branding segregation
        primary_color: '#1976d2',
        secondary_color: '#dc004e',
      };
      return this.currentBranding;
    } finally {
      this.brandingPromise = null;
    }
  }

  /**
   * Load branding from API
   */
  private async loadBranding(): Promise<Branding> {
    try {
      // For localhost, try to get customer-specific branding if logged in
      const hostname = window.location.hostname;
      const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
      
      let endpoint = '/endpoints/branding';
      let params: any = { 
        path: 'current',
        _t: Date.now() // Cache buster
      };
      
      // If on localhost and user is logged in, get customer branding
      if (isLocalhost) {
        const token = localStorage.getItem('access_token');
        if (token) {
          // Try to get customer-specific branding
          endpoint = '/endpoints/branding/customer';
        }
      }
      
      const response = await api.get(endpoint, { params });
      
      if (response.data.success) {
        return response.data.data;
      }
      
      // If API returns but without success, throw error
      throw new Error('Branding configuration not found');
    } catch (error) {
      console.error('Failed to load branding:', error);
      // Re-throw the error to be caught by getCurrentBranding
      throw error;
    }
  }

  /**
   * Get default branding - NOT USED (handled in getCurrentBranding catch)
   */
  private getDefaultBranding(): Branding {
    // This method is no longer used - fallback is handled in getCurrentBranding
    return {
      domain: window.location.hostname,
      brand_name: 'Portal',
      logo_url: '',
      favicon_url: '', // Empty favicon to maintain multi-branding segregation
      primary_color: '#1976d2',
      secondary_color: '#dc004e',
    };
  }

  /**
   * Apply branding to the page
   */
  async applyBranding(): Promise<void> {
    const branding = await this.getCurrentBranding();

    // Update favicon
    this.updateFavicon(branding.favicon_url);

    // Update page title
    document.title = `${branding.brand_name} - Portal`;

    // Apply custom CSS if provided
    if (branding.custom_css) {
      this.applyCustomCSS(branding.custom_css);
    }

    // Update theme colors in meta tags
    this.updateMetaTags(branding);
  }

  /**
   * Update favicon
   */
  private updateFavicon(url: string) {
    // Remove existing favicons
    const existingLinks = document.querySelectorAll("link[rel*='icon']");
    existingLinks.forEach(link => link.remove());

    // Only add new favicon if URL is provided (not empty)
    // This maintains multi-branding segregation by not defaulting to a generic favicon
    if (url && url.trim() !== '') {
      // Add new favicon
      const link = document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'icon';
      link.href = url;
      document.head.appendChild(link);

      // Also update apple-touch-icon if it's a PNG
      if (url.endsWith('.png')) {
        const appleLink = document.createElement('link');
        appleLink.rel = 'apple-touch-icon';
        appleLink.href = url;
        document.head.appendChild(appleLink);
      }
    }
    // If no URL provided, favicons remain removed for multi-branding segregation
  }

  /**
   * Apply custom CSS
   */
  private applyCustomCSS(css: string) {
    // Remove existing custom branding styles
    const existingStyle = document.getElementById('custom-branding-css');
    if (existingStyle) {
      existingStyle.remove();
    }

    // Add new styles
    const style = document.createElement('style');
    style.id = 'custom-branding-css';
    style.textContent = css;
    document.head.appendChild(style);
  }

  /**
   * Update meta tags for theme colors
   */
  private updateMetaTags(branding: Branding) {
    // Update theme-color meta tag
    let themeColorMeta = document.querySelector("meta[name='theme-color']");
    if (!themeColorMeta) {
      themeColorMeta = document.createElement('meta');
      themeColorMeta.setAttribute('name', 'theme-color');
      document.head.appendChild(themeColorMeta);
    }
    themeColorMeta.setAttribute('content', branding.primary_color);

    // Update manifest theme colors
    const manifest = document.querySelector("link[rel='manifest']") as HTMLLinkElement;
    if (manifest) {
      // Note: In a real implementation, you'd need to dynamically generate
      // the manifest with the correct colors
      console.log('Manifest update needed for dynamic branding');
    }
  }

  /**
   * Clear cached branding (useful when switching domains or updating)
   */
  clearCache() {
    this.currentBranding = null;
    this.brandingPromise = null;
  }
  
  /**
   * Force reload branding
   */
  async reloadBranding(): Promise<void> {
    this.clearCache();
    await this.applyBranding();
  }
}

// Export singleton instance
export default new BrandingService();